import { minorUnitsPerMajor } from '@wix/ai-scheduling-assistant-common-lib/utils';
import { logPurchaseSuccess } from './appsflyer';
import { GtmEvent, pushGtmEvent } from './gtm';

/**
 * Report one confirmed payment to both web revenue destinations.
 *
 * The caller owns the once-only boundary and must call this only for a `paid` verdict — a `saved`
 * setup stored a card but moved no money.
 *
 * ⚠️ **`amountMinor` is MINOR units and every destination wants MAJOR ones.** The hosted lane feeds
 * these same destinations through `getCheckoutSessionStatus`, which already divides
 * (`toMajorUnits`), so a USD 29.99 hosted payment arrives as `29.99` while the Elements lane's
 * `amountMinor` arrives as `2999`. Reporting the raw minor value put a 100x figure into AppsFlyer's
 * `af_revenue` and into GTM's purchase tag for every Elements payment — silently, because nothing
 * downstream knows which lane a number came from. Converted here, at the one place both lanes
 * converge, rather than at each destination.
 *
 * Divided by the currency's own minor-unit count, never a hardcoded 100: JPY and KRW are
 * zero-decimal, so `¥5000` is `amountMinor: 5000` and dividing by 100 would report ¥50.
 */
export function reportPurchaseSuccess(
  amountMinor: number | null | undefined,
  currency: string | null | undefined,
  origin?: string,
): { amount: number; currency: string } | null {
  if (typeof amountMinor === 'number' && Number.isFinite(amountMinor) && currency) {
    const amount = amountMinor / minorUnitsPerMajor(currency);
    logPurchaseSuccess(amount, currency, undefined, origin);
    pushGtmEvent(GtmEvent.purchaseSuccess, { value: amount, currency });
    // The MAJOR-unit amount, handed back so the caller's `purchase_verified` BI reports the same number
    // `af_purchase` just did rather than converting a second time.
    return { amount, currency };
  }
  // A 100%-off payment still carries value:0. Only a genuinely absent amount falls back to the
  // bare event, preserving the hosted lane's existing behavior.
  pushGtmEvent(GtmEvent.purchaseSuccess, {});
  return null;
}
